'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { ArrowLeft, Mail, Lock, AlertCircle, CheckCircle2 } from 'lucide-react'
import Link from 'next/link'

type Step = 'email' | 'verify' | 'success' | 'error'

const serverUrl = process.env.NEXT_PUBLIC_SERVER_URL?.replace(/\/$/, '') ?? ''

const getApiUrl = (path: string) => {
  return serverUrl ? `${serverUrl}${path}` : path
}

export default function DeleteAccountPage() {
  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleRequestDeletion = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const response = await fetch(getApiUrl('/auth/delete-account/request'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.message || 'Failed to request account deletion')
      }

      setStep('verify')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
      setStep('error')
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmDeletion = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const response = await fetch(getApiUrl('/auth/delete-account/confirm'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.message || 'Invalid verification code')
      }

      setStep('success')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
      setStep('error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="border-b border-border/50 bg-white">
        <div className="max-w-2xl mx-auto px-6 py-6 flex items-center">
          <Link href="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Back to Home</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Email Request Step */}
          {step === 'email' && (
            <Card className="p-8 border border-border/50">
              <div className="space-y-6">
                <div className="space-y-2">
                  <h1 className="text-2xl font-bold text-foreground">Delete Your Account</h1>
                  <p className="text-muted-foreground">
                    We&apos;ll send a verification code to your email to confirm the deletion.
                  </p>
                </div>

                <form onSubmit={handleRequestDeletion} className="space-y-4">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                    <input
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-border/50 focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20 transition"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || !email}
                    className="w-full bg-destructive hover:bg-destructive/90 text-white font-bold"
                  >
                    {loading ? 'Sending...' : 'Send Verification Code'}
                  </Button>
                </form>

                <p className="text-xs text-muted-foreground text-center">
                  This action cannot be undone. All your data will be permanently deleted.
                </p>
              </div>
            </Card>
          )}

          {/* Verify Code Step */}
          {step === 'verify' && (
            <Card className="p-8 border border-border/50">
              <div className="space-y-6">
                <div className="space-y-2">
                  <h1 className="text-2xl font-bold text-foreground">Verify Deletion</h1>
                  <p className="text-muted-foreground">
                    Enter the 6-digit code sent to <span className="font-medium text-foreground">{email}</span>
                  </p>
                </div>

                <form onSubmit={handleConfirmDeletion} className="space-y-4">
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                    <input
                      type="text"
                      placeholder="000000"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-border/50 focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20 transition text-center text-lg font-mono tracking-widest"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || code.length !== 6}
                    className="w-full bg-destructive hover:bg-destructive/90 text-white font-bold"
                  >
                    {loading ? 'Confirming...' : 'Confirm & Delete Account'}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setStep('email')
                      setCode('')
                      setError('')
                    }}
                    className="w-full"
                  >
                    Back
                  </Button>
                </form>
              </div>
            </Card>
          )}

          {/* Success Step */}
          {step === 'success' && (
            <Card className="p-8 border border-border/50">
              <div className="space-y-6 text-center">
                <div className="flex justify-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                </div>

                <div className="space-y-2">
                  <h1 className="text-2xl font-bold text-foreground">Account Deleted</h1>
                  <p className="text-muted-foreground">
                    Your account and all associated data have been permanently deleted. We&apos;re sorry to see you go.
                  </p>
                </div>

                <Link href="/">
                  <Button className="w-full bg-primary hover:bg-accent text-white font-bold">
                    Return to Home
                  </Button>
                </Link>
              </div>
            </Card>
          )}

          {/* Error Step */}
          {step === 'error' && (
            <Card className="p-8 border border-destructive/50 bg-destructive/5">
              <div className="space-y-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-destructive flex-shrink-0 mt-0.5" />
                  <div className="space-y-2">
                    <h1 className="text-2xl font-bold text-foreground">Something Went Wrong</h1>
                    <p className="text-muted-foreground">{error || 'An error occurred during the deletion process.'}</p>
                  </div>
                </div>

                <Button
                  onClick={() => {
                    setStep('email')
                    setEmail('')
                    setCode('')
                    setError('')
                  }}
                  className="w-full bg-primary hover:bg-accent text-white font-bold"
                >
                  Try Again
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>
    </main>
  )
}
